import React from "react";
import { Formik, Form, Field, FieldProps } from "formik";
import * as Yup from "yup";
import { Box, Checkbox, Flex, Spinner, Text, Textarea } from "@chakra-ui/react";
import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import { useMeeting } from "../../hooks/useMeeting";

interface MeetingOverrideModalProps {
  evalId: number;
  isOpen: boolean;
  rfxId: number;
  onClose: () => void;
  onOverrideComplete: () => void;
}

interface OverrideFormValues {
  confirmed: boolean;
  reason: string;
}

const validationSchema = Yup.object({
  reason: Yup.string()
    .required("Reason is required.")
    .min(10, "Reason must be at least 10 characters.")
    .max(2000, "Reason must be at most 2000 characters."),
  confirmed: Yup.boolean()
    .required()
    .oneOf([true], "You must confirm before proceeding."),
});

const initialValues: OverrideFormValues = {
  reason: "",
  confirmed: false,
};

export const MeetingOverrideModal: React.FC<MeetingOverrideModalProps> = ({
  rfxId,
  evalId,
  isOpen,
  onClose,
  onOverrideComplete,
}) => {
  const { overrideMeeting } = useMeeting(rfxId, evalId);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        overrideMeeting.mutate(
          { reason: values.reason, confirmed: values.confirmed },
          {
            onSuccess: () => {
              resetForm();
              onClose();
              onOverrideComplete();
            },
            onSettled: () => {
              setSubmitting(false);
            },
          }
        );
      }}
    >
      {({ values, errors, touched, isSubmitting, isValid, dirty, handleSubmit, resetForm }) => {
        const canProceed = values.confirmed && values.reason.length >= 10;

        const handleClose = () => {
          if (!isSubmitting) {
            resetForm();
            onClose();
          }
        };

        return (
          <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Skip Panel Meeting"
            size="md"
            modalType="alert"
            iconType="warning"
            closeOnOverlayClick={!isSubmitting}
            closeOnEsc={!isSubmitting}
            customFooter={
              <Flex gap={3} justify="flex-end" w="100%">
                <Button
                  btntype="secondary"
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  btntype="primary"
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={!canProceed || isSubmitting}
                  data-testid="override-proceed-btn"
                >
                  {isSubmitting ? (
                    <Flex align="center" gap={2}>
                      <Spinner size="sm" />
                      Processing...
                    </Flex>
                  ) : (
                    "Proceed"
                  )}
                </Button>
              </Flex>
            }
          >
            <Form>
              <Box className="space-y-4">
                <Text fontSize="sm" color="gray.700">
                  Skipping the panel meeting means consensus scores will not be
                  recorded. You will proceed directly to award decisions using
                  pre-meeting scores.
                </Text>

                <Field name="reason">
                  {({ field }: FieldProps<string>) => (
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        mb={1}
                        htmlFor="override-reason"
                        as="label"
                      >
                        Reason for skipping
                      </Text>
                      <Textarea
                        {...field}
                        id="override-reason"
                        placeholder="Enter a reason (minimum 10 characters)"
                        rows={4}
                        isDisabled={isSubmitting}
                        data-testid="override-reason-input"
                      />
                      {touched.reason && errors.reason && (
                        <Text fontSize="xs" color="red.500" mt={1}>
                          {errors.reason}
                        </Text>
                      )}
                    </Box>
                  )}
                </Field>

                <Field name="confirmed">
                  {({ field }: FieldProps<boolean>) => (
                    <Box>
                      <Checkbox
                        id="override-confirmed"
                        isChecked={field.value}
                        onChange={field.onChange}
                        name={field.name}
                        isDisabled={isSubmitting}
                        colorScheme="blue"
                        data-testid="override-confirm-checkbox"
                      >
                        <Text fontSize="sm">
                          I understand that skipping the panel meeting means
                          consensus scores will not be recorded
                        </Text>
                      </Checkbox>
                      {touched.confirmed && errors.confirmed && (
                        <Text fontSize="xs" color="red.500" mt={1}>
                          {errors.confirmed}
                        </Text>
                      )}
                    </Box>
                  )}
                </Field>
              </Box>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};
